/* eslint-disable i18next/no-literal-string */
import React, { useMemo, useState } from 'react';
import { useObserver } from 'mobx-react';
import { object, string } from 'yup';
import useTranslation from 'hooks/use-translation';
import Form from 'components/forms/form';
import useDutchiePayEnrollment from 'hooks/use-dutchiePay-enrollment';
import { useDutchiePayAnalytics } from 'src/payments/hooks/use-dutchie-pay-analytics/use-dutchie-pay-analytics';

// TODO: Colocate this mutation with this view
import { useCreateMxEnrollment } from 'src/payments/utils/mx-utils';
import { ViewTextProps } from 'src/payments/dutchie-pay/signup';
import { EnrollmentSteps } from 'src/payments/constants';
import { DutchiePayHeader, DutchiePayTextBody, DutchiePayButtonContainer, LegalFooter } from '../../../../components';

import { AddressAutocomplete } from './address-autocomplete';

export type AddressLabelInput = {
  addressLine1: string;
  addressLine2: string;
  back: string;
  city: string;
  continue: string;
  state: string;
  zip: string;
};

export type SubmitAddressInput = {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  zip: string;
};

export function AddressForm({ titleText }: ViewTextProps): JSX.Element {
  const { t } = useTranslation();
  const DutchiePayEnrollment = useDutchiePayEnrollment();
  const { createMxEnrollmentMutation } = useCreateMxEnrollment();
  const { trackEnrollmentStepSubmitted } = useDutchiePayAnalytics();

  const billingAddress = useObserver(() => DutchiePayEnrollment.billingAddress);

  const [prefilledValues, setPrefilledValues] = useState({
    addressLine1: billingAddress?.line1 ?? '',
    addressLine2: billingAddress?.line2 ?? '',
    city: billingAddress?.city ?? '',
    state: billingAddress?.state ?? '',
    zip: billingAddress?.postalCode ?? '',
  });

  const handleSubmit = (vals): void => {
    trackEnrollmentStepSubmitted(EnrollmentSteps.addressForm);

    void createMxEnrollmentMutation(vals);
  };

  const HEADINGS = useMemo(
    () => ({
      main: titleText ?? t('dutchiePay.addAddress.heading', 'Home address'),
      subHeading: t('dutchiePay.addAddress.subHeading', 'This will be used to verify your identity.'),
    }),
    [t, titleText]
  );

  const LABELS = useMemo(
    () => ({
      addressLine1: t('common.addressLine1', 'Street address'),
      addressLine2: t('common.addressLine2', 'Apt, Ste, Unit (optional)'),
      city: t('common.city', 'City'),
      state: t('common.state', 'State'),
      zip: t('common.zip', 'Zip code'),
      back: t('common.back', 'Back'),
      continue: t('common.continue', 'Continue'),
    }),
    [t]
  );

  const addressValidation = useMemo(
    () =>
      object().shape({
        addressLine1: string().required(t('form.streetAddressRequired', 'Street address is required')),
        city: string().required(t('form.city', 'City is required')),
        state: string().required(t('form.state', 'State is required')),
        zip: string().required(t('form.zip', 'Postal code is required')),
      }),
    [t]
  );

  return (
    <>
      <Form onSubmit={handleSubmit} validationSchema={addressValidation} prefilledValues={prefilledValues}>
        <DutchiePayHeader>{HEADINGS.main}</DutchiePayHeader>
        <DutchiePayTextBody>{HEADINGS.subHeading}</DutchiePayTextBody>
        <AddressAutocomplete
          setPrefilledValues={setPrefilledValues}
          labels={LABELS}
          prefilledValues={prefilledValues}
        />

        <DutchiePayButtonContainer showBackLink={false} requiredFieldKeys={['addressLine1', 'city', 'state', 'zip']} />
      </Form>

      <LegalFooter />
    </>
  );
}
